<script setup lang="ts">
import SharedPartialFooter from '@pkg/site-chrome/components/PartialFooter.vue';
import { type SocialLink as SocialLinkType } from '@pkg/sanity-utils/linkQueries';
import { AppStore as AppStoreEnum } from '@pkg/sanity-utils/constants/appstore';
import { type LinkType } from '@pkg/link';

/**
 * App-local entry for Nuxt auto-import. Shell + shared chrome live in @pkg/site-chrome;
 * www-only assets (logo SVG, social icons, app stores) stay here.
 * The wordmark defaults from the shared footer.
 */
interface Props {
  pageLinks?: LinkType[];
  socialLinks?: SocialLinkType[];
  legalLinks?: LinkType[];
  footnote?: string;
}

const localePath = useLocalePath();

const props = withDefaults(defineProps<Props>(), {
  socialLinks: () => [],
  pageLinks: () => [],
  legalLinks: () => [],
  footnote: () => '',
});
</script>

<template>
  <SharedPartialFooter
    variant="full"
    :page-links="props.pageLinks"
    :social-links="props.socialLinks"
    :legal-links="props.legalLinks"
    :footnote="props.footnote"
  >
    <template #logo>
      <NuxtLink :to="localePath('/')">
        <img
          width="80"
          height="64"
          loading="lazy"
          src="~/assets/images/logo.svg"
          alt="Too Good To Go Logo"
        >
      </NuxtLink>
    </template>

    <template #social>
      <SocialLink
        v-for="link in props.socialLinks"
        :key="link.url"
        :link="link"
        :aria-label="`Visit Too Good To Go's ${link.title}`"
      />
    </template>

    <template #app-stores>
      <AppStore :app-store="AppStoreEnum.APPLE_APP_STORE" />
      <AppStore :app-store="AppStoreEnum.GOOGLE_PLAY_STORE" />
      <AppStore :app-store="AppStoreEnum.HUAWEI_APP_GALLERY" />
    </template>
  </SharedPartialFooter>
</template>
